import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import AdminLayout from "../components/AdminLayout";
import { useAuth } from "../context/AuthContext";
import { Plus, Edit2, Trash2, Upload, X, Eye, EyeOff, Save } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "salami", label: "Sallam" },
  { value: "salcice", label: "Salçiçe" },
  { value: "qofte", label: "Qofte/Hamburger" },
  { value: "parizier", label: "Parizier" },
  { value: "other", label: "Tjetër" },
];

const emptyProduct = {
  name_sq: "", name_en: "",
  ingredients_sq: "", ingredients_en: "",
  origin_sq: "Importi", origin_en: "Import",
  storage_sq: "Të ruhet në temp. 1-5°C", storage_en: "Store at temp. 1-5°C",
  notes_sq: "", notes_en: "",
  nutrition: { energy: "", fat: "", saturated: "", carbs: "", sugar: "", protein: "", fiber: "", salt: "" },
  images: [], category: "salami", active: true, order: 0,
};

const ProductForm = ({ product, onClose, onSaved, authHeaders }) => {
  const [data, setData] = useState(product || emptyProduct);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isEdit = !!product?.id;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setNutr = (k, v) => setData((d) => ({ ...d, nutrition: { ...d.nutrition, [k]: v } }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Foto më e madhe se 100MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      set("images", [...data.images, res.url]);
      toast.success("Foto u ngarkua");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gabim në ngarkim");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => set("images", data.images.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...data };
      delete payload.id;
      delete payload.slug;
      delete payload.created_at;
      if (isEdit) {
        await axios.put(`${API}/admin/products/${product.id}`, payload, { headers: authHeaders() });
        toast.success("Produkti u përditësua");
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: authHeaders() });
        toast.success("Produkti u shtua");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gabim në ruajtje");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="product-form-modal">
      <form onSubmit={onSubmit} className="bg-white w-full max-w-3xl">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {isEdit ? "Ndrysho Produktin" : "Shto Produkt"}
          </h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-zinc-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Names */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Emri (SQ) *</label>
              <input data-testid="product-name-sq" required value={data.name_sq} onChange={(e) => set("name_sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Emri (EN) *</label>
              <input data-testid="product-name-en" required value={data.name_en} onChange={(e) => set("name_en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
          </div>

          {/* Category + active */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Kategoria</label>
              <select data-testid="product-category" value={data.category} onChange={(e) => set("category", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Renditja</label>
              <input type="number" value={data.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="product-active" checked={data.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4" />
                <span className="text-sm font-medium">Aktiv (i dukshëm)</span>
              </label>
            </div>
          </div>

          {/* Ingredients */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Përbërësit (SQ)</label>
              <textarea rows={3} value={data.ingredients_sq} onChange={(e) => set("ingredients_sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ingredients (EN)</label>
              <textarea rows={3} value={data.ingredients_en} onChange={(e) => set("ingredients_en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none resize-none" />
            </div>
          </div>

          {/* Origin / Storage */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Origjina (SQ)</label>
              <input value={data.origin_sq} onChange={(e) => set("origin_sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Origin (EN)</label>
              <input value={data.origin_en} onChange={(e) => set("origin_en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ruajtja (SQ)</label>
              <input value={data.storage_sq} onChange={(e) => set("storage_sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Storage (EN)</label>
              <input value={data.storage_en} onChange={(e) => set("storage_en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
          </div>

          {/* Notes */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Shënime (SQ)</label>
              <input value={data.notes_sq} onChange={(e) => set("notes_sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes (EN)</label>
              <input value={data.notes_en} onChange={(e) => set("notes_en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
          </div>

          {/* Nutrition */}
          <div>
            <label className="block text-sm font-bold mb-2 uppercase tracking-wider text-zinc-600">Vlerat Ushqyese / 100g</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {["energy", "fat", "saturated", "carbs", "sugar", "protein", "fiber", "salt"].map((k) => (
                <div key={k}>
                  <label className="block text-xs text-zinc-500 mb-1 capitalize">{k}</label>
                  <input value={data.nutrition[k] || ""} onChange={(e) => setNutr(k, e.target.value)} className="w-full border border-zinc-300 px-2 py-1.5 text-sm focus:border-[#0A0A0A] focus:outline-none" />
                </div>
              ))}
            </div>
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium mb-2">Fotot ({data.images.length})</label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
              {data.images.map((url, i) => (
                <div key={i} className="relative group aspect-square bg-zinc-100">
                  <img src={url} alt="" loading="lazy" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label className="aspect-square border-2 border-dashed border-zinc-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#0A0A0A] transition-colors">
                <Upload className="w-5 h-5 text-zinc-400 mb-1" />
                <span className="text-xs text-zinc-500">{uploading ? "..." : "Ngarko"}</span>
                <input data-testid="product-image-upload" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} disabled={uploading} className="hidden" />
              </label>
            </div>
            <p className="text-xs text-zinc-500">JPG, PNG, WEBP. Max 100MB.</p>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-zinc-200 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="flex-1 px-6 py-3 border border-zinc-300 hover:bg-zinc-50">Anulo</button>
          <button data-testid="product-save-button" type="submit" disabled={saving} className="flex-1 px-6 py-3 bg-[#0A0A0A] text-white font-medium hover:bg-zinc-800 disabled:opacity-50 flex items-center justify-center gap-2">
            <Save className="w-4 h-4" />
            {saving ? "Duke ruajtur..." : "Ruaj"}
          </button>
        </div>
      </form>
    </div>
  );
};

const AdminProducts = () => {
  const { authHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {id...}=edit
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products`, { headers: authHeaders() });
      setProducts(data);
    } catch (e) {
      toast.error("Gabim duke ngarkuar produktet");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (p) => {
    if (!window.confirm(`Fshi "${p.name_sq}"?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, { headers: authHeaders() });
      toast.success("Produkti u fshi");
      load();
    } catch {
      toast.error("Gabim në fshirje");
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/admin/products/${p.id}`, { ...p, active: !p.active }, { headers: authHeaders() });
      toast.success(p.active ? "Çaktivizuar" : "Aktivizuar");
      load();
    } catch {
      toast.error("Gabim");
    }
  };

  const filtered = filter ? products.filter((p) => p.name_sq.toLowerCase().includes(filter.toLowerCase()) || p.name_en.toLowerCase().includes(filter.toLowerCase())) : products;

  return (
    <AdminLayout>
      <div data-testid="admin-products-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Produktet</h1>
            <p className="text-zinc-600 mt-1">Menaxho produktet e Sallamerisë</p>
          </div>
          <button data-testid="add-product-button" onClick={() => setEditing({})} className="bg-[#0A0A0A] text-white px-5 py-3 font-medium hover:bg-zinc-800 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Shto Produkt
          </button>
        </div>

        <input
          placeholder="Kërko produkte..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full max-w-md mb-6 border border-zinc-300 px-4 py-2 focus:border-[#0A0A0A] focus:outline-none"
        />

        {loading ? (
          <div className="text-zinc-500">Duke ngarkuar...</div>
        ) : (
          <div className="bg-white border border-zinc-200">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-zinc-600">
                <tr>
                  <th className="p-4">Foto</th>
                  <th className="p-4">Emri</th>
                  <th className="p-4">Kategoria</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Veprime</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid="product-row">
                    <td className="p-4">
                      <div className="w-12 h-12 bg-zinc-100 overflow-hidden">
                        {p.images?.[0] ? <img src={p.images[0]} alt="" loading="lazy" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-400 text-lg font-bold">{p.name_sq?.[0]}</div>}
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-[#0A0A0A]">{p.name_sq}</p>
                      <p className="text-xs text-zinc-500">{p.name_en}</p>
                    </td>
                    <td className="p-4 text-sm text-zinc-600">{CATEGORIES.find((c) => c.value === p.category)?.label || p.category}</td>
                    <td className="p-4">
                      <button onClick={() => toggleActive(p)} className={`inline-flex items-center gap-1 text-xs px-2 py-1 ${p.active ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                        {p.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {p.active ? "Aktiv" : "I fshehur"}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button data-testid="edit-product-button" onClick={() => setEditing(p)} className="p-2 hover:bg-zinc-100"><Edit2 className="w-4 h-4" /></button>
                        <button data-testid="delete-product-button" onClick={() => onDelete(p)} className="p-2 hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-zinc-500">Asnjë produkt</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {editing !== null && (
          <ProductForm
            product={editing.id ? editing : null}
            authHeaders={authHeaders}
            onClose={() => setEditing(null)}
            onSaved={load}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
